import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';
import {
    Check, Upload, CheckCircle, Edit, RefreshCw, Loader2,
    Image as ImageIcon, Video, Eye, Trash2, Save, ArrowLeft,
    X, Plus
} from 'lucide-react';
import AuthContext from '../context/AuthContext';
import Base from '../components/Base';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Input, Tabs, Badge, Alert, Spinner, Modal } from '../components/ui'; 

// Import images for the templates
import img1 from '../templates/template_1.png';
import img2 from '../templates/template_2.png';
import img3 from '../templates/template_3.png';
import img4 from '../templates/template_4.jpg';
import img5 from '../templates/template_5.png';
import img6 from '../templates/template_6.jpg';
import img7 from '../templates/template_7.jpg';
import img8 from '../templates/template_8.jpg';
import img9 from '../templates/template_9.jpg';
// Placeholder images for new templates (will be replaced with actual screenshots)
const img10 = 'https://placehold.co/800x600/E5E7EB/1F2937?text=Grid+Gallery';
const img11 = 'https://placehold.co/800x600/6366F1/FFFFFF?text=Hero+Features';
const img12 = 'https://placehold.co/800x600/A78BFA/FFFFFF?text=Stories+Trust';
const img13 = 'https://placehold.co/800x600/1F2937/FFFFFF?text=Split+Vision';
const img14 = 'https://placehold.co/800x600/374151/FFFFFF?text=Magazine'; 

const Templates = () => {
    console.log("Templates component is rendering!");
    const { authToken, loading } = useContext(AuthContext);
    const navigate = useNavigate(); 
    const [currentStep, setCurrentStep] = useState('select'); 
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [savedConfig, setSavedConfig] = useState(null);
    const [hoveredTemplateId, setHoveredTemplateId] = useState(null); 
    
    // Form data states
    const [sets, setSets] = useState(Array.from({ length: 4 }, (_, i) => ({ 
        id: i + 1, name: '', gender: '', type: '', category: '', 
        image: { file: null, preview: null } 
    })));
    // Banner Images with description field
    const [bannerImages, setBannerImages] = useState(Array.from({ length: 3 }, (_, i) => ({
        id: i + 1, name: '', gender: '', type: '', category: '', description: '',
        file: null, preview: null
    })));

    // Sliding Banners - keep as before (no description)
    const [slidingBanners, setSlidingBanners] = useState(Array.from({ length: 2 }, (_, i) => ({
        id: i + 1, name: '', gender: '', type: '', category: '',
        file: null, preview: null
    })));

    // Promo Banners - keep as before
    const [promoBanners, setPromoBanners] = useState(Array.from({ length: 3 }, (_, i) => ({
        id: i + 1, text: '', sub_text: '', name: '', gender: '', type: '', category: '',
        image: { file: null, preview: null }
    })));

    // Fashion Trends - keep as before
    const [fashionTrends, setFashionTrends] = useState(Array.from({ length: 4 }, (_, i) => ({
        id: i + 1, text: '', name: '', gender: '', type: '', category: '',
        image: { file: null, preview: null }
    })));

    // Videos - REMOVE all metadata, just file upload
    const [videos, setVideos] = useState(Array.from({ length: 5 }, (_, i) => ({
        id: i + 1,
        file: null, preview: null
    })));

    // GENERIC STATE - Reused across templates
    // Hero Section (T10-14)
    const [hero, setHero] = useState({
        title: '', subtitle: '', description: '',
        image: { name: '', gender: '', type: '', category: '', file: null, preview: null }
    });

    // Collections (T11, 12, 13) - CHANGE to match Product Categories structure
    const [collections, setCollections] = useState(Array.from({ length: 3 }, (_, i) => ({
        id: i + 1, name: '', gender: '', type: '', category: '',
        image: { file: null, preview: null }
    })));

    // Statement Section (T11, 12, 14)
    const [statement, setStatement] = useState({
        title: '', subtitle: '',
        image: { name: '', gender: '', type: '', category: '', file: null, preview: null }
    });

    // Workshop Section (T13)
    const [workshop, setWorkshop] = useState({
        title: '', description: '',
        image: { name: '', gender: '', type: '', category: '', file: null, preview: null }
    });

    // Gallery Section (T14)
    const [gallery, setGallery] = useState(Array.from({ length: 8 }, (_, i) => ({
        id: i + 1,
        image: { name: '', gender: '', type: '', category: '', file: null, preview: null }
    })));

    // Instagram Feed (T14)
    const [instagram, setInstagram] = useState(Array.from({ length: 6 }, (_, i) => ({
        id: i + 1,
        image: { name: '', gender: '', type: '', category: '', file: null, preview: null }
    })));

    const [previewImage, setPreviewImage] = useState(null);

    const templates = [
        { id: 1, name: 'Classic', image: 'https://d3qvi06vol9hrb.cloudfront.net/template_1.png', description: 'A clean, timeless layout perfect for any store.' },
        { id: 2, name: 'Modern', image: 'https://d3qvi06vol9hrb.cloudfront.net/template_2.png', description: 'Dynamic categories with a sleek, contemporary design.' },
        { id: 3, name: 'Banner', image: 'https://d3qvi06vol9hrb.cloudfront.net/template_3.png', description: 'Eye-catching banners to highlight your categories.' },
        { id: 4, name: 'Promo', image: 'https://d3qvi06vol9hrb.cloudfront.net/template_4.jpg', description: 'Promotional sections to engage your customers.' },
        { id: 5, name: 'Dynamic Promo', image: 'https://d3qvi06vol9hrb.cloudfront.net/template_5.jpg', description: 'Advanced layout with videos and rich promotional content.' },
        { id: 6, name: 'Minimalist', image: 'https://d3qvi06vol9hrb.cloudfront.net/template_6.jpg', description: 'A simple, clean design focused on products.' },
        { id: 7, name: 'Vogue', image: 'https://d3qvi06vol9hrb.cloudfront.net/template_7.jpg', description: 'A sophisticated layout for high-end fashion.' },
        { id: 8, name: 'Vintage', image: 'https://d3qvi06vol9hrb.cloudfront.net/template_8.jpg', description: 'A retro-inspired design with classic elements.' },
        { id: 9, name: 'Elegant', image: 'https://d3qvi06vol9hrb.cloudfront.net/template_9.jpg', description: 'A refined template for luxury brands.' },
        { id: 10, name: 'Dawn', image: img10, description: 'Clean, minimalist design with modern hero sections. Perfect for any fashion brand.' },
        { id: 11, name: 'Prestige', image: img11, description: 'Luxury dark theme with elegant full-screen hero. Ideal for high-end fashion.' },
        { id: 12, name: 'Impact', image: img12, description: 'Bold typography and vibrant colors. Makes a statement with every click.' },
        { id: 13, name: 'Craft', image: img13, description: 'Warm, artisan-focused design. Perfect for handmade and sustainable brands.' },
        { id: 14, name: 'Studio', image: img14, description: 'Visual-first photography layout. Showcase your products like art.' }
    ];

    // Dropdown mappings with "All" option
    const typeOptionsMap = {
        Men: ['Indianwear', 'Westernwear', 'All'],
        Women: ['Indianwear', 'Westernwear', 'All'],
        Kids: ['Boys', 'Girls', 'All'],
        All: ['All'] // When gender is "All", only show "All" for type
    };

    const categoryOptionsMap = {
        Men: {
            Indianwear: ['Sherwanis', 'Kurtas', 'Nehru Jackets', 'All'],
            Westernwear: ['Blazers', 'Suits', 'Trousers', 'All'],
            All: ['All'] // When type is "All", only show "All" for category
        },
        Women: {
            Indianwear: ['Sarees', 'Lehengas', 'Kurtis', 'All'],
            Westernwear: ['Dresses', 'Tops', 'Jumpsuits', 'All'],
            All: ['All']
        },
        Kids: {
            Boys: ['Top Wear', 'Bottom Wear', 'All'],
            Girls: ['Frocks', 'Top Wear', 'Bottom Wear', 'All'],
            All: ['All']
        },
        All: {
            All: ['All'] // When gender is "All", show "All" for everything
        }
    };

    const authAxios = React.useMemo(() => {
        const instance = axios.create({
            baseURL: 'https://vasstrinfo.com/api',
            headers: { Authorization: `Bearer ${authToken?.access || ''}` },
            withCredentials: true,
        });
        instance.interceptors.request.use(config => {
            if (['post', 'put', 'patch', 'delete'].includes(config.method.toLowerCase())) {
                const csrfToken = Cookies.get('csrftoken');
                if (csrfToken) config.headers['X-CSRFToken'] = csrfToken;
            }
            return config;
        });
        return instance;
    }, [authToken?.access]);

    const fetchCsrfToken = useCallback(async () => {
        try {
            await authAxios.get('/get_csrf_token/');
        } catch (err) {
            Swal.fire({ 
                icon: 'error', 
                title: 'Connection Error', 
                text: 'Could not connect to the server. Please refresh and try again.',
                confirmButtonColor: '#f97316' 
            });
            throw err;
        }
    }, [authAxios]);

    const fetchTemplateConfiguration = useCallback(async () => {
        if (!authToken?.access) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        try {
            const response = await authAxios.get('/get_template_configuration/');
            if (response.status === 200 && response.data.template_number) {
                const data = response.data;
                const templateNum = parseInt(data.template_number);
                setSelectedTemplate(templateNum);
                setSavedConfig(data);
                setCurrentStep('view');
                loadConfigurationData(data, templateNum);
            }
        } catch (error) {
            if (error.response?.status !== 404) {
                console.error('Failed to load existing configuration:', error);
            }
        } finally {
            setIsLoading(false);
        }
    }, [authAxios, authToken?.access]);

    const loadConfigurationData = (data, templateNum) => {
        const timestamp = `?t=${new Date().getTime()}`;
        const getUrl = (path) => path ? path + timestamp : null;

        setSets(Array.from({ length: 4 }, (_, i) => ({
            id: i + 1,
            name: data[`set${i+1}_name`] || '',
            gender: data[`set${i+1}_gender`] || '',
            type: data[`set${i+1}_type`] || '',
            category: data[`set${i+1}_category`] || '',
            image: { file: null, preview: getUrl(data[`set${i+1}_image`]) }
        })));

        if ([3, 6, 7].includes(templateNum)) {
            setBannerImages(Array.from({ length: 3 }, (_, i) => ({
                id: i + 1,
                file: null, 
                preview: getUrl(data[`banner${i+1}_image`])
            })));
        }

        if ([4, 5, 8, 9].includes(templateNum)) {
            setSlidingBanners(Array.from({ length: 2 }, (_, i) => ({
                id: i + 1,
                file: null, 
                preview: getUrl(data[`sliding_banner${i+1}_image`])
            })));
            
            setPromoBanners(Array.from({ length: 3 }, (_, i) => ({
                id: i + 1,
                text: data[`promo_banner${i+1}_text`] || '',
                sub_text: data[`promo_banner${i+1}_sub_text`] || '',
                gender: data[`promo_banner${i+1}_gender`] || '',
                type: data[`promo_banner${i+1}_type`] || '',
                category: data[`promo_banner${i+1}_category`] || '',
                image: { file: null, preview: getUrl(data[`promo_banner${i+1}_image`]) }
            })));
            
            setFashionTrends(Array.from({ length: 4 }, (_, i) => ({
                id: i + 1,
                text: data[`fashion_trend${i+1}_text`] || '',
                gender: data[`fashion_trend${i+1}_gender`] || '',
                type: data[`fashion_trend${i+1}_type`] || '',
                category: data[`fashion_trend${i+1}_category`] || '',
                image: { file: null, preview: getUrl(data[`fashion_trend${i+1}_image`]) }
            })));
            
            setVideos(Array.from({ length: 5 }, (_, i) => ({
                id: i + 1,
                file: null, 
                preview: getUrl(data[`video${i+1}`])
            })));
        }
    };

    useEffect(() => {
        if (!loading && authToken) {
            fetchCsrfToken().then(fetchTemplateConfiguration).catch(() => setIsLoading(false));
        } else if (!loading) {
            setIsLoading(false);
        }
    }, [loading, authToken, fetchCsrfToken, fetchTemplateConfiguration]);

    const handleSelectTemplate = async (templateId) => {
        const template = templates.find(t => t.id === templateId);
        
        try {
            setIsSubmitting(true);
            await fetchCsrfToken();
            
            const formData = new FormData();
            formData.append('template_number', templateId);
            
            await authAxios.post('/select_template/', formData);
            setSelectedTemplate(templateId);
            
            // Reset states for new template
            resetStatesForTemplate(templateId);
            setCurrentStep('configure');
            
            Swal.fire({
                icon: 'success',
                title: 'Template Selected!',
                text: `You can now configure the ${template.name} template.`,
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.error || 'Failed to select template.',
                confirmButtonColor: '#f97316'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleViewTemplate = (templateId) => {
        navigate(`/Template${templateId}`);
    };

    const resetStatesForTemplate = (templateId) => {
        // Product Categories - keep as before
        setSets(Array.from({ length: 4 }, (_, i) => ({
            id: i + 1, name: '', gender: '', type: '', category: '',
            image: { file: null, preview: null }
        })));

        // Banner Images - ADD description
        setBannerImages(Array.from({ length: 3 }, (_, i) => ({
            id: i + 1, name: '', gender: '', type: '', category: '', description: '',
            file: null, preview: null
        })));

        // Sliding Banners - keep as before (no description)
        setSlidingBanners(Array.from({ length: 2 }, (_, i) => ({
            id: i + 1, name: '', gender: '', type: '', category: '',
            file: null, preview: null
        })));

        // Promo Banners - keep as before
        setPromoBanners(Array.from({ length: 3 }, (_, i) => ({
            id: i + 1, text: '', sub_text: '', name: '', gender: '', type: '', category: '',
            image: { file: null, preview: null }
        })));

        // Fashion Trends - keep as before
        setFashionTrends(Array.from({ length: 4 }, (_, i) => ({
            id: i + 1, text: '', name: '', gender: '', type: '', category: '',
            image: { file: null, preview: null }
        })));

        // Videos - REMOVE all metadata
        setVideos(Array.from({ length: 5 }, (_, i) => ({
            id: i + 1,
            file: null, preview: null
        })));

        // Hero - ADD description to image
        setHero({
            title: '', subtitle: '', description: '',
            image: { name: '', gender: '', type: '', category: '', description: '', file: null, preview: null }
        });

        // Collections - CHANGE to Product Categories structure
        setCollections(Array.from({ length: 3 }, (_, i) => ({
            id: i + 1, name: '', gender: '', type: '', category: '',
            image: { file: null, preview: null }
        })));
        setStatement({
            title: '', subtitle: '',
            image: { name: '', gender: '', type: '', category: '', file: null, preview: null }
        });
        setWorkshop({
            title: '', description: '',
            image: { name: '', gender: '', type: '', category: '', file: null, preview: null }
        });
        setGallery(Array.from({ length: 8 }, (_, i) => ({
            id: i + 1,
            image: { name: '', gender: '', type: '', category: '', file: null, preview: null }
        })));
        setInstagram(Array.from({ length: 6 }, (_, i) => ({
            id: i + 1,
            image: { name: '', gender: '', type: '', category: '', file: null, preview: null }
        })));
    };

    const handleFileUpload = (event, setter, index, field = null, specialType = null) => {
        const file = event.target.files[0];
        if (!file) return;

        const isVideo = file.type.startsWith('video/');
        const isImage = file.type.startsWith('image/');

        if (!isVideo && !isImage) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid File Type',
                text: 'Please upload a valid image or video file.',
                confirmButtonColor: '#f97316'
            });
            return;
        }

        const maxSize = isVideo ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
        if (file.size > maxSize) {
            Swal.fire({
                icon: 'error',
                title: 'File Too Large',
                text: `File size should not exceed ${isVideo ? '10MB' : '5MB'}.`,
                confirmButtonColor: '#f97316'
            });
            return;
        }

        const previewUrl = URL.createObjectURL(file);

        // Handle special cases for sections
        if (setter) {
            setter(prev => prev.map((item, i) => {
                if (i === index) {
                    if (field) {
                        return { ...item, [field]: { file, preview: previewUrl } };
                    } else {
                        return { ...item, file, preview: previewUrl };
                    }
                }
                return item;
            }));
        }
    };

    const handleInputChange = (setter, index, field, value) => {
        setter(prev => prev.map((item, i) => {
            if (i === index) {
                const updatedItem = { ...item, [field]: value };
                if (field === 'gender') {
                    updatedItem.type = '';
                    updatedItem.category = '';
                } else if (field === 'type') {
                    updatedItem.category = '';
                }
                return updatedItem;
            }
            return item;
        }));
    };

    const clearImage = (setter, index, field = null) => {
        setter(prev => prev.map((item, i) => {
            if (i === index) {
                if (field) {
                    return { ...item, [field]: { file: null, preview: null } };
                } else {
                    return { ...item, file: null, preview: null };
                }
            }
            return item;
        }));
    };

    const handleReview = () => {
        setCurrentStep('review');
    };

    const handleBackToEdit = () => {
        setCurrentStep('configure');
    };

    const handleSaveConfiguration = async () => {
        setIsSubmitting(true);

        // Show loading popup
        Swal.fire({
            title: 'Saving Configuration...',
            html: 'Please wait while we save your template settings.',
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        try {
            await fetchCsrfToken();

            const formData = new FormData();
            formData.append('template_number', selectedTemplate);

            // Add sets data for templates 2-9
            if ([2, 3, 4, 5, 6, 7, 8, 9].includes(selectedTemplate)) {
                const validSets = sets.map(s => ({
                    id: s.id,
                    name: s.name || '',
                    gender: s.gender || '',
                    type: s.type || '',
                    category: s.category || ''
                }));

                formData.append('sets', JSON.stringify(validSets));
                sets.forEach(s => {
                    if (s.image.file) {
                        formData.append(`set${s.id}_image_file`, s.image.file);
                    }
                });
            }

            // Add banner images for templates 3, 6, 7 - WITH DESCRIPTION
            if ([3, 6, 7].includes(selectedTemplate)) {
                const validBanners = bannerImages.map(b => ({
                    id: b.id,
                    name: b.name || '',
                    gender: b.gender || '',
                    type: b.type || '',
                    category: b.category || '',
                    description: b.description || '' // NEW: description field
                }));
                formData.append('banners', JSON.stringify(validBanners));
                bannerImages.forEach((b, i) => {
                    if (b.file) {
                        formData.append(`banner${i + 1}_image_file`, b.file);
                    }
                });
            }

            // Add advanced content for templates 4, 5, 8, 9
            if ([4, 5, 8, 9].includes(selectedTemplate)) {
                // Sliding banners
                const validSlidingBanners = slidingBanners.map(b => ({
                    id: b.id,
                    name: b.name || '',
                    gender: b.gender || '',
                    type: b.type || '',
                    category: b.category || ''
                }));
                formData.append('sliding_banners', JSON.stringify(validSlidingBanners));
                slidingBanners.forEach((b, i) => {
                    if (b.file) {
                        formData.append(`sliding_banner${i + 1}_image_file`, b.file);
                    }
                });

                // Promo banners
                const validPromoBanners = promoBanners.map(b => ({
                    id: b.id,
                    text: b.text || '',
                    sub_text: b.sub_text || '',
                    name: b.name || '',
                    gender: b.gender || '',
                    type: b.type || '',
                    category: b.category || ''
                }));
                
                formData.append('promo_banners', JSON.stringify(validPromoBanners));
                promoBanners.forEach(b => {
                    if (b.image.file) {
                        formData.append(`promo_banner${b.id}_image_file`, b.image.file);
                    }
                });

                // Fashion trends
                const validFashionTrends = fashionTrends.map(t => ({
                    id: t.id,
                    text: t.text || '',
                    name: t.name || '',
                    gender: t.gender || '',
                    type: t.type || '',
                    category: t.category || ''
                }));
                
                formData.append('fashion_trends', JSON.stringify(validFashionTrends));
                fashionTrends.forEach(t => {
                    if (t.image.file) {
                        formData.append(`fashion_trend${t.id}_image_file`, t.image.file);
                    }
                });

                // Videos - SIMPLE FILE UPLOAD ONLY (no metadata)
                videos.forEach((v, i) => {
                    if (v.file) {
                        formData.append(`video${i + 1}_file`, v.file);
                    }
                });
            }

            // Template 10 (DAWN) - Using GENERIC fields
            if (selectedTemplate === 10) {
                // Hero section (GENERIC: hero_*)
                if (hero.title) formData.append('hero_title', hero.title);
                if (hero.subtitle) formData.append('hero_subtitle', hero.subtitle);
                if (hero.image.file) formData.append('hero_image_file', hero.image.file);
                // Hero image metadata
                if (hero.image.name) formData.append('hero_image_name', hero.image.name);
                if (hero.image.gender) formData.append('hero_image_gender', hero.image.gender);
                if (hero.image.type) formData.append('hero_image_type', hero.image.type);
                if (hero.image.category) formData.append('hero_image_category', hero.image.category);

                // Categories (GENERIC: set1-3) - FULL metadata (same as Product Categories)
                sets.slice(0, 3).forEach((cat, i) => {
                    if (cat.name) formData.append(`set${i + 1}_name`, cat.name);
                    if (cat.gender) formData.append(`set${i + 1}_gender`, cat.gender);
                    if (cat.type) formData.append(`set${i + 1}_type`, cat.type);
                    if (cat.category) formData.append(`set${i + 1}_category`, cat.category);
                    if (cat.image.file) formData.append(`set${i + 1}_image_file`, cat.image.file);
                });
            }

            // Template 11 (PRESTIGE) - Using GENERIC fields
            if (selectedTemplate === 11) {
                // Hero section (GENERIC: hero_*)
                if (hero.title) formData.append('hero_title', hero.title);
                if (hero.subtitle) formData.append('hero_subtitle', hero.subtitle);
                if (hero.description) formData.append('hero_description', hero.description);
                if (hero.image.file) formData.append('hero_image_file', hero.image.file);
                // Hero image metadata
                if (hero.image.name) formData.append('hero_image_name', hero.image.name);
                if (hero.image.gender) formData.append('hero_image_gender', hero.image.gender);
                if (hero.image.type) formData.append('hero_image_type', hero.image.type);
                if (hero.image.category) formData.append('hero_image_category', hero.image.category);

                // Collections (GENERIC: collection1-2) - NOW using category structure
                const validCollections = collections.slice(0, 2).map((col, i) => ({
                    id: i + 1,
                    name: col.name || '',
                    gender: col.gender || '',
                    type: col.type || '',
                    category: col.category || ''
                }));
                formData.append('collections', JSON.stringify(validCollections));
                collections.slice(0, 2).forEach((col, i) => {
                    if (col.name) formData.append(`collection${i + 1}_name`, col.name);
                    if (col.gender) formData.append(`collection${i + 1}_gender`, col.gender);
                    if (col.type) formData.append(`collection${i + 1}_type`, col.type);
                    if (col.category) formData.append(`collection${i + 1}_category`, col.category);
                    if (col.image.file) formData.append(`collection${i + 1}_image_file`, col.image.file);
                });

                // Statement section (GENERIC: statement_*)
                if (statement.title) formData.append('statement_title', statement.title);
                if (statement.subtitle) formData.append('statement_subtitle', statement.subtitle);
                if (statement.image.file) formData.append('statement_image_file', statement.image.file);
                // Statement image metadata
                if (statement.image.name) formData.append('statement_image_name', statement.image.name);
                if (statement.image.gender) formData.append('statement_image_gender', statement.image.gender);
                if (statement.image.type) formData.append('statement_image_type', statement.image.type);
                if (statement.image.category) formData.append('statement_image_category', statement.image.category);

                // Categories (GENERIC: set1-3) - FULL metadata (same as Product Categories)
                sets.slice(0, 3).forEach((cat, i) => {
                    if (cat.name) formData.append(`set${i + 1}_name`, cat.name);
                    if (cat.gender) formData.append(`set${i + 1}_gender`, cat.gender);
                    if (cat.type) formData.append(`set${i + 1}_type`, cat.type);
                    if (cat.category) formData.append(`set${i + 1}_category`, cat.category);
                    if (cat.image.file) formData.append(`set${i + 1}_image_file`, cat.image.file);
                });
            }

            // Template 12 (IMPACT) - Using GENERIC fields
            if (selectedTemplate === 12) {
                // Hero section (GENERIC: hero_* - text only, NO image)
                if (hero.title) formData.append('hero_title', hero.title);
                if (hero.subtitle) formData.append('hero_subtitle', hero.subtitle);

                // Collections (GENERIC: collection1-2) - NOW using category structure
                const validCollections = collections.slice(0, 2).map((col, i) => ({
                    id: i + 1,
                    name: col.name || '',
                    gender: col.gender || '',
                    type: col.type || '',
                    category: col.category || ''
                }));
                formData.append('collections', JSON.stringify(validCollections));
                collections.slice(0, 2).forEach((col, i) => {
                    if (col.name) formData.append(`collection${i + 1}_name`, col.name);
                    if (col.gender) formData.append(`collection${i + 1}_gender`, col.gender);
                    if (col.type) formData.append(`collection${i + 1}_type`, col.type);
                    if (col.category) formData.append(`collection${i + 1}_category`, col.category);
                    if (col.image.file) formData.append(`collection${i + 1}_image_file`, col.image.file);
                });

                // Statement section (GENERIC: statement_* - text only, NO image)
                if (statement.title) formData.append('statement_title', statement.title);
                if (statement.subtitle) formData.append('statement_subtitle', statement.subtitle);

                // Categories (GENERIC: set1-3) - FULL metadata (same as Product Categories)
                sets.slice(0, 3).forEach((cat, i) => {
                    if (cat.name) formData.append(`set${i + 1}_name`, cat.name);
                    if (cat.gender) formData.append(`set${i + 1}_gender`, cat.gender);
                    if (cat.type) formData.append(`set${i + 1}_type`, cat.type);
                    if (cat.category) formData.append(`set${i + 1}_category`, cat.category);
                    if (cat.image.file) formData.append(`set${i + 1}_image_file`, cat.image.file);
                });
            }

            // Template 13 (CRAFT) - Using GENERIC fields
            if (selectedTemplate === 13) {
                // Hero section (GENERIC: hero_*)
                if (hero.title) formData.append('hero_title', hero.title);
                if (hero.subtitle) formData.append('hero_subtitle', hero.subtitle);
                if (hero.description) formData.append('hero_description', hero.description);
                if (hero.image.file) formData.append('hero_image_file', hero.image.file);
                // Hero image metadata
                if (hero.image.name) formData.append('hero_image_name', hero.image.name);
                if (hero.image.gender) formData.append('hero_image_gender', hero.image.gender);
                if (hero.image.type) formData.append('hero_image_type', hero.image.type);
                if (hero.image.category) formData.append('hero_image_category', hero.image.category);

                // Collections (GENERIC: collection1-3) - NOW using category structure
                const validCollections = collections.map((col, i) => ({
                    id: i + 1,
                    name: col.name || '',
                    gender: col.gender || '',
                    type: col.type || '',
                    category: col.category || ''
                }));
                formData.append('collections', JSON.stringify(validCollections));
                collections.forEach((col, i) => {
                    if (col.name) formData.append(`collection${i + 1}_name`, col.name);
                    if (col.gender) formData.append(`collection${i + 1}_gender`, col.gender);
                    if (col.type) formData.append(`collection${i + 1}_type`, col.type);
                    if (col.category) formData.append(`collection${i + 1}_category`, col.category);
                    if (col.image.file) formData.append(`collection${i + 1}_image_file`, col.image.file);
                });

                // Workshop section (GENERIC: workshop_*)
                if (workshop.title) formData.append('workshop_title', workshop.title);
                if (workshop.description) formData.append('workshop_description', workshop.description);
                if (workshop.image.file) formData.append('workshop_image_file', workshop.image.file);
                // Workshop image metadata
                if (workshop.image.name) formData.append('workshop_image_name', workshop.image.name);
                if (workshop.image.gender) formData.append('workshop_image_gender', workshop.image.gender);
                if (workshop.image.type) formData.append('workshop_image_type', workshop.image.type);
                if (workshop.image.category) formData.append('workshop_image_category', workshop.image.category);
            }

            // Template 14 (STUDIO) - Using GENERIC fields
            if (selectedTemplate === 14) {
                // Hero section (GENERIC: hero_*)
                if (hero.title) formData.append('hero_title', hero.title);
                if (hero.subtitle) formData.append('hero_subtitle', hero.subtitle);
                if (hero.description) formData.append('hero_description', hero.description);
                if (hero.image.file) formData.append('hero_image_file', hero.image.file);
                // Hero image metadata
                if (hero.image.name) formData.append('hero_image_name', hero.image.name);
                if (hero.image.gender) formData.append('hero_image_gender', hero.image.gender);
                if (hero.image.type) formData.append('hero_image_type', hero.image.type);
                if (hero.image.category) formData.append('hero_image_category', hero.image.category);

                // Gallery (GENERIC: gallery1-8)
                const validGallery = gallery.map(img => ({
                    id: img.id,
                    name: img.image?.name || '',
                    gender: img.image?.gender || '',
                    type: img.image?.type || '',
                    category: img.image?.category || ''
                }));
                formData.append('gallery', JSON.stringify(validGallery));
                gallery.forEach((img, i) => {
                    if (img.image.file) formData.append(`gallery${i + 1}_image_file`, img.image.file);
                });

                // Statement section (GENERIC: statement_*)
                if (statement.title) formData.append('statement_title', statement.title);
                if (statement.subtitle) formData.append('statement_subtitle', statement.subtitle);
                if (statement.image.file) formData.append('statement_image_file', statement.image.file);
                // Statement image metadata
                if (statement.image.name) formData.append('statement_image_name', statement.image.name);
                if (statement.image.gender) formData.append('statement_image_gender', statement.image.gender);
                if (statement.image.type) formData.append('statement_image_type', statement.image.type);
                if (statement.image.category) formData.append('statement_image_category', statement.image.category);

                // Instagram feed (GENERIC: instagram1-6)
                const validInstagram = instagram.map(img => ({
                    id: img.id,
                    name: img.image?.name || '',
                    gender: img.image?.gender || '',
                    type: img.image?.type || '',
                    category: img.image?.category || ''
                }));
                formData.append('instagram', JSON.stringify(validInstagram));
                instagram.forEach((img, i) => {
                    if (img.image.file) formData.append(`instagram${i + 1}_image_file`, img.image.file);
                });
            }

            const response = await authAxios.post('/save_template_configuration/', formData);
            setSavedConfig(response.data.data);
            setCurrentStep('view');
            
            Swal.fire({
                icon: 'success',
                title: 'Configuration Saved!',
                text: 'Your template has been successfully configured.',
                timer: 2000,
                showConfirmButton: false
            });

        } catch (error) {
            console.error('Save error:', error.response?.data);
            Swal.fire({
                icon: 'error',
                title: 'Save Failed',
                text: error.response?.data?.error || 'An unexpected error occurred.',
                confirmButtonColor: '#f97316'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderSkeleton = () => (
        <div className="p-6 max-w-7xl mx-auto animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto mb-8"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                {Array.from({ length: 9 }).map((_, i) => (
                    <div key={i} className="bg-gray-200 rounded-2xl h-64"></div>
                ))}
            </div>
        </div>
    );

    const renderUploadCard = (title, item, onUpload, onClear, index, isVideo = false, dimensions = '') => (
        <div className="bg-gray-50 rounded-xl p-4 border-2 border-dashed border-gray-300 hover:border-orange-400 transition-colors">
            <h4 className="text-sm font-medium text-gray-700 mb-1">{title}</h4>
            {dimensions && (
                <p className="text-xs text-gray-500 mb-2">📐 {dimensions}</p>
            )}
            <div className="relative group">
                {item.preview ? (
                    <div className="relative">
                        {isVideo ? (
                            <video
                                src={item.preview}
                                className="w-full h-32 object-cover rounded-lg"
                                controls
                                muted
                            />
                        ) : (
                            <img
                                src={item.preview}
                                alt={title}
                                className="w-full h-32 object-cover rounded-lg cursor-pointer"
                                onClick={() => setPreviewImage(item.preview)}
                            />
                        )}
                        <button
                            onClick={() => onClear()}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                            <X size={14} />
                        </button>
                    </div>
                ) : (
                    <div
                        className="h-32 flex flex-col items-center justify-center cursor-pointer text-gray-500 hover:text-orange-600"
                        onClick={() => document.getElementById(`upload-${title}-${index}`).click()}
                    >
                        {isVideo ? <Video size={24} /> : <Upload size={24} />}
                        <span className="text-xs mt-2">Click to upload</span>
                        {dimensions && (
                            <span className="text-xs text-gray-400 mt-1">{dimensions}</span>
                        )}
                    </div>
                )}
                <input
                    id={`upload-${title}-${index}`}
                    type="file"
                    accept={isVideo ? "video/*" : "image/*"}
                    onChange={onUpload}
                    className="hidden"
                />
            </div>
        </div>
    );

    // ============================================================
    // ENHANCED UPLOAD CARD WITH METADATA
    // Handles: name, gender, type, category, description (optional)
    // "All" Selection Logic:
    //   - gender="All" → type=null, category=null
    //   - type="All" → category=null
    // ============================================================
    const renderUploadCardWithMetadata = (title, item, setter, index, isVideo = false, dimensions = '', useImageField = false, showDescription = false) => {
        // Extract values based on item structure
        const filePreview = useImageField ? item.image?.preview : item.preview;
        const fileName = useImageField ? (item.image?.name || item.name || '') : (item.name || '');
        const gender = item.gender || '';
        const type = item.type || '';
        const category = item.category || '';
        const description = item.description || '';

        const handleUpload = (e) => {
            const file = e.target.files[0];
            if (!file) return;

            const isVid = file.type.startsWith('video/');
            const isImg = file.type.startsWith('image/');

            if (!isVid && !isImg) {
                Swal.fire({
                    icon: 'error',
                    title: 'Invalid File Type',
                    text: 'Please upload a valid image or video file.',
                    confirmButtonColor: '#f97316'
                });
                return;
            }

            const maxSize = isVid ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
            if (file.size > maxSize) {
                Swal.fire({
                    icon: 'error',
                    title: 'File Too Large',
                    text: `File size should not exceed ${isVid ? '10MB' : '5MB'}.`,
                    confirmButtonColor: '#f97316'
                });
                return;
            }

            const previewUrl = URL.createObjectURL(file);

            setter(prev => prev.map((itm, i) => {
                if (i === index) {
                    if (useImageField) {
                        return { ...itm, image: { ...itm.image, file, preview: previewUrl } };
                    } else {
                        return { ...itm, file, preview: previewUrl };
                    }
                }
                return itm;
            }));
        };

        const handleClear = () => {
            setter(prev => prev.map((itm, i) => {
                if (i === index) {
                    if (useImageField) {
                        return { ...itm, image: { ...itm.image, file: null, preview: null } };
                    } else {
                        return { ...itm, file: null, preview: null };
                    }
                }
                return itm;
            }));
        };

        const handleMetadataChange = (field, value) => {
            setter(prev => prev.map((itm, i) => {
                if (i === index) {
                    const updated = { ...itm, [field]: value };

                    // "All" Selection Logic
                    if (field === 'gender') {
                        if (value === 'All') {
                            // If gender is "All", set type and category to null
                            updated.type = '';
                            updated.category = '';
                        } else {
                            // Reset type and category when gender changes
                            updated.type = '';
                            updated.category = '';
                        }
                    } else if (field === 'type') {
                        if (value === 'All') {
                            // If type is "All", set category to null
                            updated.category = '';
                        } else {
                            // Reset category when type changes
                            updated.category = '';
                        }
                    }

                    // Also update image object if using that structure
                    if (useImageField && itm.image) {
                        updated.image = { ...itm.image, [field]: value };
                    }
                    return updated;
                }
                return itm;
            }));
        };

        return (
            <div className="bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-orange-400 transition-colors shadow-sm">
                <h4 className="text-sm font-semibold text-gray-800 mb-3 border-b pb-2">{title}</h4>
                {dimensions && (
                    <p className="text-xs text-gray-500 mb-2">📐 {dimensions}</p>
                )}

                {/* Image/Video Upload Section */}
                <div className="relative group mb-4">
                    {filePreview ? (
                        <div className="relative">
                            {isVideo ? (
                                <video
                                    src={filePreview}
                                    className="w-full h-32 object-cover rounded-lg"
                                    controls
                                    muted
                                />
                            ) : (
                                <img
                                    src={filePreview}
                                    alt={title}
                                    className="w-full h-32 object-cover rounded-lg cursor-pointer"
                                    onClick={() => setPreviewImage(filePreview)}
                                />
                            )}
                            <button
                                onClick={handleClear}
                                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    ) : (
                        <div
                            className="h-32 flex flex-col items-center justify-center cursor-pointer text-gray-500 hover:text-orange-600 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-400 transition-colors"
                            onClick={() => document.getElementById(`upload-meta-${title}-${index}`).click()}
                        >
                            {isVideo ? <Video size={24} /> : <Upload size={24} />}
                            <span className="text-xs mt-2 font-medium">Click to upload</span>
                        </div>
                    )}
                    <input
                        id={`upload-meta-${title}-${index}`}
                        type="file"
                        accept={isVideo ? "video/*" : "image/*"}
                        onChange={handleUpload}
                        className="hidden"
                    />
                </div>

                {/* Metadata Fields */}
                <div className="space-y-3">
                    {/* Name Field */}
                    <input
                        type="text"
                        placeholder="Image Name/Title"
                        value={fileName}
                        onChange={(e) => handleMetadataChange('name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />

                    {/* Gender Dropdown with "All" option */}
                    <select
                        value={gender}
                        onChange={(e) => handleMetadataChange('gender', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                        <option value="">Select Gender</option>
                        <option value="Men">Men</option>
                        <option value="Women">Women</option>
                        <option value="Kids">Kids</option>
                        <option value="All">All</option>
                    </select>

                    {/* Type Dropdown - disabled if gender is not selected or if gender="All" */}
                    <select
                        value={type}
                        onChange={(e) => handleMetadataChange('type', e.target.value)}
                        disabled={!gender || gender === 'All'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                        <option value="">Select Type</option>
                        {(typeOptionsMap[gender] || []).map(t => (
                            <option key={t} value={t}>{t}</option>
                        ))}
                    </select>

                    {/* Category Dropdown - disabled if type is not selected or if type="All" */}
                    <select
                        value={category}
                        onChange={(e) => handleMetadataChange('category', e.target.value)}
                        disabled={!type || type === 'All'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                        <option value="">Select Category</option>
                        {(categoryOptionsMap[gender]?.[type] || []).map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>

                    {/* Description Field - Only shown when showDescription=true */}
                    {showDescription && (
                        <textarea
                            placeholder="Description (Optional)"
                            value={description}
                            onChange={(e) => handleMetadataChange('description', e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                        />
                    )}
                </div>
            </div>
        );
    };

    const renderTemplateSelection = () => (
        <div className="p-6 max-w-7xl mx-auto">
            <h1 className="text-3xl font-bold text-center mb-4">Choose Your Website Template</h1>
            <p className="text-gray-600 text-center mb-8">
                Select a template that best fits your store's style. You can customize it in the next step.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {templates.map(template => (
                    <div
                        key={template.id}
                        onMouseEnter={() => setHoveredTemplateId(template.id)}
                        onMouseLeave={() => setHoveredTemplateId(null)}
                        className={`relative bg-white rounded-2xl shadow-lg transition-all duration-300 ${
                            selectedTemplate === template.id ? 'ring-4 ring-orange-500' : 'hover:shadow-xl hover:scale-105'
                        }`}
                    >
                        <div className="w-full h-48 overflow-hidden rounded-t-2xl">
                            <img 
                                src={template.image} 
                                alt={template.name} 
                                className="w-full h-full object-cover" 
                            />
                        </div>
                        {selectedTemplate === template.id && (
                            <div className="absolute top-4 right-4 bg-orange-500 rounded-full p-2 text-white">
                                <Check size={20} />
                            </div>
                        )}
                        <div className="p-4">
                            <h3 className="text-lg font-semibold text-gray-800">{template.name}</h3>
                            <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                        </div>

                        {hoveredTemplateId === template.id && (
                            <div className="absolute inset-0 bg-black bg-opacity-50 rounded-2xl flex flex-col items-center justify-center gap-4 transition-opacity duration-300 opacity-100">
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleViewTemplate(template.id); }}
                                    className="px-6 py-3 bg-white text-gray-800 rounded-lg shadow-md hover:bg-gray-100 transition-colors flex items-center gap-2"
                                >
                                    <Eye size={18} /> View
                                </button>
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleSelectTemplate(template.id); }}
                                    disabled={isSubmitting}
                                    className="px-6 py-3 bg-orange-500 text-white rounded-lg shadow-md hover:bg-orange-600 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSubmitting && selectedTemplate === template.id ? (
                                        <>
                                            <Loader2 className="animate-spin" size={18} /> Selecting...
                                        </>
                                    ) : (
                                        <>
                                            <Check size={18} /> Select
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>
            
            {isSubmitting && (
                <div className="flex justify-center mt-8">
                    <div className="flex items-center gap-2 text-orange-600">
                        <Loader2 className="animate-spin" size={20} />
                        <span>Selecting template...</span>
                    </div>
                </div>
            )}
        </div>
    );

    /**
     * PRODUCT CATEGORIES SECTION - Used by ALL templates (T1-T12)
     *
     * This section is CONSISTENT across all templates - no variations!
     * Every category has the SAME structure:
     * - Name (text input)
     * - Gender (Men/Women/Kids/All dropdown)
     * - Type (Indianwear/Westernwear/Boys/Girls/All dropdown - cascading based on gender)
     * - Category (Sub-category dropdown - cascading based on type)
     * - Image (file upload)
     *
     * @param {number} count - Number of categories to show (4 for T1-9, 3 for T10-12)
     * @param {string} imageDimensions - Image size hint text (e.g., '600x600px' or '400x500px')
     */
    const renderCategoriesSection = (count = 4, imageDimensions = '600×600px (1:1 Square)') => (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Product Categories (Optional)</h2>
            <p className="text-gray-600 text-sm mb-4">
                Add categories with full metadata: name, gender, type, sub-category, and image.
            </p>
            <div className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-${Math.min(count, 4)} gap-6`}>
                {sets.slice(0, count).map((set, index) => (
                    <div key={set.id} className="space-y-4">
                        {/* Category Header */}
                        <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">
                            Category {set.id}
                        </h3>

                        {/* Category Name Input */}
                        <input
                            type="text"
                            placeholder="Category Name"
                            value={set.name}
                            onChange={(e) => handleInputChange(setSets, index, 'name', e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />

                        {/* Gender Dropdown */}
                        <select
                            value={set.gender}
                            onChange={(e) => handleInputChange(setSets, index, 'gender', e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        >
                            <option value="">Select Gender</option>
                            <option value="Men">Men</option>
                            <option value="Women">Women</option>
                            <option value="Kids">Kids</option>
                            <option value="All">All</option>
                        </select>

                        {/* Type Dropdown - Cascading (disabled until gender selected) */}
                        <select
                            value={set.type}
                            onChange={(e) => handleInputChange(setSets, index, 'type', e.target.value)}
                            disabled={!set.gender}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100"
                        >
                            <option value="">Select Type</option>
                            {(typeOptionsMap[set.gender] || []).map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>

                        {/* Sub-Category Dropdown - Cascading (disabled until type selected) */}
                        <select
                            value={set.category}
                            onChange={(e) => handleInputChange(setSets, index, 'category', e.target.value)}
                            disabled={!set.type}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100"
                        >
                            <option value="">Select Sub-Category</option>
                            {(categoryOptionsMap[set.gender]?.[set.type] || []).map(category => (
                                <option key={category} value={category}>{category}</option>
                            ))}
                        </select>

                        {/* Category Image Upload */}
                        {renderUploadCard(
                            `Category ${set.id} Image`,
                            set.image,
                            (e) => handleFileUpload(e, setSets, index, 'image'),
                            () => clearImage(setSets, index, 'image'),
                            set.id,
                            false,
                            imageDimensions
                        )}
                    </div>
                ))}
            </div>
        </div>
    );

    const renderBannersSection = () => (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Banner Images (Optional)</h2>
            <p className="text-gray-600 text-sm mb-4">Upload banner images with category information and description.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {bannerImages.map((banner, index) => (
                    <div key={banner.id}>
                        {renderUploadCardWithMetadata(
                            `Banner ${banner.id}`,
                            banner,
                            setBannerImages,
                            index,
                            false,
                            '1920×600px (Wide Banner)',
                            false,
                            true // showDescription=true for banners
                        )}
                    </div>
                ))}
            </div>
        </div>
    );

    const renderSlidingBannersSection = () => (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Sliding Banners (Optional)</h2>
            <p className="text-gray-600 text-sm mb-4">Upload sliding banner images with category information.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {slidingBanners.map((banner, index) => (
                    <div key={banner.id}>
                        {renderUploadCardWithMetadata(
                            `Sliding Banner ${banner.id}`,
                            banner,
                            setSlidingBanners,
                            index,
                            false,
                            '1920×800px (Wide Banner)',
                            false
                        )}
                    </div>
                ))}
            </div>
        </div>
    );

    const renderPromoBannersSection = () => (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Promotional Banners (Optional)</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {promoBanners.map((banner, index) => (
                    <div key={banner.id} className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">
                            Promo Banner {banner.id}
                        </h3>
                        
                        <input
                            type="text"
                            placeholder="Promo Text"
                            value={banner.text}
                            onChange={(e) => handleInputChange(setPromoBanners, index, 'text', e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                        
                        <input
                            type="text"
                            placeholder="Sub Text"
                            value={banner.sub_text}
                            onChange={(e) => handleInputChange(setPromoBanners, index, 'sub_text', e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                        
                        <select
                            value={banner.gender}
                            onChange={(e) => handleInputChange(setPromoBanners, index, 'gender', e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        >
                            <option value="">Select Gender</option>
                            <option value="Men">Men</option>
                            <option value="Women">Women</option>
                            <option value="Kids">Kids</option>
                        </select>
                        
                        <select
                            value={banner.type}
                            onChange={(e) => handleInputChange(setPromoBanners, index, 'type', e.target.value)}
                            disabled={!banner.gender}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100"
                        >
                            <option value="">Select Type</option>
                            {(typeOptionsMap[banner.gender] || []).map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                        
                        <select
                            value={banner.category}
                            onChange={(e) => handleInputChange(setPromoBanners, index, 'category', e.target.value)}
                            disabled={!banner.type}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100"
                        >
                            <option value="">Select Sub-Category</option>
                            {(categoryOptionsMap[banner.gender]?.[banner.type] || []).map(category => (
                                <option key={category} value={category}>{category}</option>
                            ))}
                        </select>
                        
                        {renderUploadCard(
                            `Promo Banner ${banner.id} Image`,
                            banner.image,
                            (e) => handleFileUpload(e, setPromoBanners, index, 'image'),
                            () => clearImage(setPromoBanners, index, 'image'),
                            banner.id
                        )}
                    </div>
                ))}
            </div>
        </div>
    );

    const renderFashionTrendsSection = () => (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Fashion Trends (Optional)</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                {fashionTrends.map((trend, index) => (
                    <div key={trend.id} className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">
                            Trend {trend.id}
                        </h3>
                        
                        <input
                            type="text"
                            placeholder="Trend Text"
                            value={trend.text}
                            onChange={(e) => handleInputChange(setFashionTrends, index, 'text', e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                        
                        <select
                            value={trend.gender}
                            onChange={(e) => handleInputChange(setFashionTrends, index, 'gender', e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        >
                            <option value="">Select Gender</option>
                            <option value="Men">Men</option>
                            <option value="Women">Women</option>
                            <option value="Kids">Kids</option>
                        </select>
                        
                        <select
                            value={trend.type}
                            onChange={(e) => handleInputChange(setFashionTrends, index, 'type', e.target.value)}
                            disabled={!trend.gender}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100"
                        >
                            <option value="">Select Type</option>
                            {(typeOptionsMap[trend.gender] || []).map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                        
                        <select
                            value={trend.category}
                            onChange={(e) => handleInputChange(setFashionTrends, index, 'category', e.target.value)}
                            disabled={!trend.type}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100"
                        >
                            <option value="">Select Sub-Category</option>
                            {(categoryOptionsMap[trend.gender]?.[trend.type] || []).map(category => (
                                <option key={category} value={category}>{category}</option>
                            ))}
                        </select>
                        
                        {renderUploadCard(
                            `Trend ${trend.id} Image`,
                            trend.image,
                            (e) => handleFileUpload(e, setFashionTrends, index, 'image'),
                            () => clearImage(setFashionTrends, index, 'image'),
                            trend.id
                        )}
                    </div>
                ))}
            </div>
        </div>
    );

    // Videos Section - Simple upload without metadata
    const renderVideosSection = () => (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Videos (Optional - Max 10MB each)</h2>
            <p className="text-gray-600 text-sm mb-4">Upload promotional videos.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-6">
                {videos.map((video, index) => (
                    <div key={video.id}>
                        {renderUploadCard(
                            `Video ${video.id}`,
                            video,
                            (e) => handleFileUpload(e, setVideos, index),
                            () => clearImage(setVideos, index),
                            video.id,
                            true, // isVideo=true
                            'Video (Max 10MB)'
                        )}
                    </div>
                ))}
            </div>
        </div>
    );

    // ========================================
    // GENERIC REUSABLE SECTION COMPONENTS
    // ========================================

    // HERO SECTION - Generic component used by T10-14
    // Hero Section - with metadata and description for image
    const renderHeroSection = (showDescription = false, showImage = true, imageDimensions = '1920×800px (21:9 Wide Banner)') => (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Hero Section</h2>
            <div className="space-y-4">
                {/* Hero Title */}
                <input
                    type="text"
                    placeholder="Hero Title"
                    value={hero.title}
                    onChange={(e) => setHero({ ...hero, title: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />

                {/* Hero Subtitle */}
                <input
                    type="text"
                    placeholder="Hero Subtitle"
                    value={hero.subtitle}
                    onChange={(e) => setHero({ ...hero, subtitle: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />

                {/* Hero Description (for content, not for image) */}
                {showDescription && (
                    <textarea
                        placeholder="Hero Description"
                        value={hero.description}
                        onChange={(e) => setHero({ ...hero, description: e.target.value })}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        rows="3"
                    />
                )}

                {/* Hero Image with metadata */}
                {showImage && (
                    <div>
                        {renderUploadCardWithMetadata(
                            'Hero Image',
                            hero,
                            (setter) => setHero(setter([hero])[0]),
                            0,
                            false,
                            imageDimensions,
                            true, // useImageField=true
                            false // showDescription=false (hero section already has description field)
                        )}
                    </div>
                )}
            </div>
        </div>
    );


    // COLLECTIONS SECTION - For T11, 12, 13
    // Collections Section - NOW uses category structure like Product Categories
    const renderCollectionsSection = (count = 2, imageDimensions = '800×900px (8:9 Portrait)') => (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Collections</h2>
            <p className="text-gray-600 text-sm mb-4">Add collections with category information.</p>
            <div className={`grid grid-cols-1 md:grid-cols-${Math.min(count, 3)} gap-6`}>
                {collections.slice(0, count).map((col, index) => (
                    <div key={col.id}>
                        {renderUploadCardWithMetadata(
                            `Collection ${col.id}`,
                            col,
                            setCollections,
                            index,
                            false,
                            imageDimensions,
                            true, // useImageField=true
                            false // showDescription=false for collections
                        )}
                    </div>
                ))}
            </div>
        </div>
    );

    // STATEMENT SECTION - For T11, 12, 14
    const renderStatementSection = (showImage = true, imageDimensions = '1920×600px (16:5 Wide Banner)') => (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Statement Section</h2>
            <div className="space-y-4">
                <input
                    type="text"
                    placeholder="Statement Title"
                    value={statement.title}
                    onChange={(e) => setStatement({ ...statement, title: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
                <input
                    type="text"
                    placeholder="Statement Subtitle"
                    value={statement.subtitle}
                    onChange={(e) => setStatement({ ...statement, subtitle: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
                {showImage && renderUploadCard(
                    'Statement Image',
                    statement.image,
                    (e) => {
                        const file = e.target.files[0];
                        if (file) {
                            const previewUrl = URL.createObjectURL(file);
                            setStatement({ ...statement, image: { file, preview: previewUrl } });
                        }
                    },
                    () => setStatement({ ...statement, image: { file: null, preview: null } }),
                    'statement',
                    false,
                    imageDimensions
                )}
            </div>
        </div>
    );

    // WORKSHOP SECTION - For T13
    const renderWorkshopSection = () => (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Workshop Section</h2>
            <div className="space-y-4">
                <input
                    type="text"
                    placeholder="Workshop Title"
                    value={workshop.title}
                    onChange={(e) => setWorkshop({ ...workshop, title: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
                <textarea
                    placeholder="Workshop Description"
                    value={workshop.description}
                    onChange={(e) => setWorkshop({ ...workshop, description: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    rows="3"
                />
                {renderUploadCard(
                    'Workshop Image',
                    workshop.image,
                    (e) => {
                        const file = e.target.files[0];
                        if (file) {
                            const previewUrl = URL.createObjectURL(file);
                            setWorkshop({ ...workshop, image: { file, preview: previewUrl } });
                        }
                    },
                    () => setWorkshop({ ...workshop, image: { file: null, preview: null } }),
                    'workshop',
                    false,
                    '1200×800px (3:2 Landscape)'
                )}
            </div>
        </div>
    );

    // GALLERY SECTION - For T14
    const renderGallerySection = () => (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Gallery Images</h2>
            <p className="text-gray-600 text-sm mb-4">Upload gallery images with category information for masonry layout.</p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {gallery.map((img, index) => (
                    <div key={img.id}>
                        {renderUploadCardWithMetadata(
                            `Gallery ${img.id}`,
                            img,
                            setGallery,
                            index,
                            false,
                            '800×800px (1:1 Square)',
                            true
                        )}
                    </div>
                ))}
            </div>
        </div>
    );

    // INSTAGRAM FEED SECTION - For T14
    const renderInstagramSection = () => (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Instagram Feed</h2>
            <p className="text-gray-600 text-sm mb-4">Upload Instagram-style images with category information.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-6">
                {instagram.map((img, index) => (
                    <div key={img.id}>
                        {renderUploadCardWithMetadata(
                            `Instagram ${img.id}`,
                            img,
                            setInstagram,
                            index,
                            false,
                            '600×600px (1:1 Square)',
                            true
                        )}
                    </div>
                ))}
            </div>
        </div>
    );

    const renderConfigurationStep = () => {
        const template = templates.find(t => t.id === selectedTemplate);
        
        return (
            <div className="p-6 max-w-7xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800">Configure {template.name} Template</h1>
                        <p className="text-gray-600 mt-2">Fill in the details you want. All fields are optional.</p>
                    </div>
                    <button
                        onClick={() => setCurrentStep('select')}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                        <ArrowLeft size={18} />
                        Back to Templates
                    </button>
                </div>

                {/* Categories Section - Available for templates 2-9 */}
                {[2, 3, 4, 5, 6, 7, 8, 9].includes(selectedTemplate) && renderCategoriesSection()}

                {/* Banner Images Section - Only for template 3, 6, 7 */}
                {[3, 6, 7].includes(selectedTemplate) && renderBannersSection()}

                {/* Sliding Banners Section - Only for templates 4,5, 8, 9 */}
                {[4, 5, 8, 9].includes(selectedTemplate) && renderSlidingBannersSection()}

                {/* Promo Banners Section - Only for templates 4,5, 8, 9 */}
                {[4, 5, 8, 9].includes(selectedTemplate) && renderPromoBannersSection()}

                {/* Fashion Trends Section - Only for templates 4,5, 8, 9 */}
                {[4, 5, 8, 9].includes(selectedTemplate) && renderFashionTrendsSection()}

                {/* Videos Section - Only for templates 4,5, 8, 9 */}
                {[4, 5, 8, 9].includes(selectedTemplate) && renderVideosSection()}

                {/* Template 10-14 Configuration Forms - GENERIC SECTIONS */}
                {selectedTemplate === 10 && (
                    <>
                        {renderHeroSection(false, true, '1920×800px (21:9 Wide Banner)')}
                        {renderCategoriesSection(3, '400×500px (4:5 Portrait)')}
                    </>
                )}

                {selectedTemplate === 11 && (
                    <>
                        {renderHeroSection(true, true, '1920×800px (21:9 Wide Banner)')}
                        {renderCollectionsSection(2, true, '800×900px (8:9 Portrait)')}
                        {renderStatementSection(true, '1920×600px (16:5 Wide Banner)')}
                        {renderCategoriesSection(3, '400×500px (4:5 Portrait)')}
                    </>
                )}

                {selectedTemplate === 12 && (
                    <>
                        {renderHeroSection(false, false)}
                        {renderCollectionsSection(2, false, '700×700px (1:1 Square)')}
                        {renderStatementSection(false)}
                        {renderCategoriesSection(3, '400×500px (4:5 Portrait)')}
                    </>
                )}

                {selectedTemplate === 13 && (
                    <>
                        {renderHeroSection(true, true, '1920×800px (21:9 Wide Banner)')}
                        {renderCollectionsSection(3, true, '600×750px (4:5 Portrait)')}
                        {renderWorkshopSection()}
                    </>
                )}

                {selectedTemplate === 14 && (
                    <>
                        {renderHeroSection(true, true, '1920×800px (21:9 Wide Banner)')}
                        {renderGallerySection()}
                        {renderStatementSection(true, '1920×600px (16:5 Wide Banner)')}
                        {renderInstagramSection()}
                    </>
                )}

                {/* Action Buttons */}
                <div className="mt-12 border-t-2 border-gray-200 pt-8">
                    <div className="bg-blue-50 rounded-lg p-6 mb-6">
                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                                    <Eye className="text-white" size={20} />
                                </div>
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-800 mb-2">Ready to Review?</h3>
                                <p className="text-gray-600 text-sm mb-4">
                                    Click the button below to preview all the details you've filled in. You can then review everything before saving your configuration to the database.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end gap-4">
                        <button
                            onClick={handleReview}
                            className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-lg font-semibold rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
                        >
                            <Eye size={22} />
                            Preview Configuration
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderReviewStep = () => {
        const template = templates.find(t => t.id === selectedTemplate);
        
        return (
            <div className="p-6 max-w-7xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800">Review Configuration</h1>
                        <p className="text-gray-600 mt-2">Review your configuration before saving.</p>
                    </div>
                    <button
                        onClick={() => setCurrentStep('configure')}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                        <Edit size={18} />
                        Back to Edit
                    </button>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">Selected Template</h2>
                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                        <img src={template.image} alt={template.name} className="w-24 h-24 object-cover rounded-lg" />
                        <div>
                            <h3 className="text-lg font-semibold">{template.name}</h3>
                            <p className="text-gray-600">{template.description}</p>
                        </div>
                    </div>
                </div>

                {/* Product Categories Preview - SAME for ALL templates (T2-T12) */}
                {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].includes(selectedTemplate) && (
                    <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                        <h2 className="text-xl font-semibold text-gray-800 mb-4">Product Categories</h2>
                        <p className="text-gray-600 text-sm mb-4">
                            Review your configured categories with full metadata.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                            {sets.filter(s => s.name || s.gender || s.image.preview).map(set => (
                                <div key={set.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <h4 className="font-semibold text-gray-800 mb-2">Category {set.id}</h4>
                                    {set.name && <p className="text-sm text-gray-600"><span className="font-medium">Name:</span> {set.name}</p>}
                                    {set.gender && <p className="text-sm text-gray-600"><span className="font-medium">Gender:</span> {set.gender}</p>}
                                    {set.type && <p className="text-sm text-gray-600"><span className="font-medium">Type:</span> {set.type}</p>}
                                    {set.category && <p className="text-sm text-gray-600"><span className="font-medium">Sub-Category:</span> {set.category}</p>}
                                    {set.image.preview && (
                                        <img
                                            src={set.image.preview}
                                            alt={set.name || `Category ${set.id}`}
                                            className="w-full h-24 object-cover rounded mt-2"
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Banner Images Preview */}
                {[3, 6, 7].includes(selectedTemplate) && bannerImages.some(b => b.preview) && (
                    <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                        <h2 className="text-xl font-semibold text-gray-800 mb-4">Banner Images</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {bannerImages.filter(b => b.preview).map(banner => (
                                <div key={banner.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <h4 className="font-semibold text-gray-800 mb-2">Banner {banner.id}</h4>
                                    {banner.name && <p className="text-sm text-gray-600"><span className="font-medium">Name:</span> {banner.name}</p>}
                                    {banner.gender && <p className="text-sm text-gray-600"><span className="font-medium">Gender:</span> {banner.gender}</p>}
                                    {banner.type && <p className="text-sm text-gray-600"><span className="font-medium">Type:</span> {banner.type}</p>}
                                    {banner.category && <p className="text-sm text-gray-600"><span className="font-medium">Category:</span> {banner.category}</p>}
                                    {banner.description && <p className="text-sm text-gray-600 italic mb-2"><span className="font-medium">Description:</span> {banner.description}</p>}
                                    <img src={banner.preview} alt={banner.name || `Banner ${banner.id}`} className="w-full h-32 object-cover rounded mt-2" />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Videos Preview - Simple display (no metadata) */}
                {[4, 5, 8, 9].includes(selectedTemplate) && videos.some(v => v.preview) && (
                    <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                        <h2 className="text-xl font-semibold text-gray-800 mb-4">Videos</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">
                            {videos.filter(v => v.preview).map(video => (
                                <div key={video.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <h4 className="font-semibold text-gray-800 mb-2">Video {video.id}</h4>
                                    <video src={video.preview} className="w-full h-32 object-cover rounded" controls muted />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Save Button - Enhanced Section */}
                <div className="mt-12 border-t-2 border-gray-200 pt-8">
                    <div className="bg-green-50 rounded-lg p-6 mb-6">
                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                                    <Save className="text-white" size={20} />
                                </div>
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-800 mb-2">Everything Looks Good?</h3>
                                <p className="text-gray-600 text-sm mb-4">
                                    Click "Save Configuration" below to save all your details to the database. You'll see a success message and then be able to view your final configured template.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end gap-4">
                        <button
                            onClick={handleSaveConfiguration}
                            disabled={isSubmitting}
                            className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white text-lg font-semibold rounded-xl hover:from-green-600 hover:to-green-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="animate-spin" size={22} />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save size={22} />
                                    Save Configuration
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderViewStep = () => {
        const template = templates.find(t => t.id === selectedTemplate);

        return (
            <div className="p-6 max-w-7xl mx-auto">
                {/* Success Banner */}
                <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-8 mb-6 text-white shadow-xl">
                    <div className="flex items-center gap-4">
                        <div className="flex-shrink-0">
                            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center animate-pulse">
                                <CheckCircle size={36} className="text-white" />
                            </div>
                        </div>
                        <div className="flex-1">
                            <h1 className="text-3xl font-bold mb-2">🎉 Configuration Saved Successfully!</h1>
                            <p className="text-green-50 text-lg">
                                Your {template.name} template has been configured and saved to the database.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800">Your Configuration</h2>
                        <p className="text-gray-600 mt-1">View, edit, or preview your configured template below.</p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={() => handleViewTemplate(selectedTemplate)}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                        >
                            <Eye size={18} />
                            View Live
                        </button>
                        <button
                            onClick={() => setCurrentStep('configure')}
                            className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                        >
                            <Edit size={18} />
                            Edit
                        </button>
                        <button
                            onClick={() => setCurrentStep('select')}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                        >
                            <RefreshCw size={18} />
                            Change Template
                        </button>
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">Active Template</h2>
                    <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
                        <img src={template.image} alt={template.name} className="w-24 h-24 object-cover rounded-lg" />
                        <div>
                            <h3 className="text-lg font-semibold text-green-800">{template.name}</h3>
                            <p className="text-green-700">{template.description}</p>
                            <div className="flex items-center gap-2 mt-2">
                                <CheckCircle size={16} className="text-green-600" />
                                <span className="text-sm text-green-600">Configuration Active</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Comprehensive Configuration Summary */}
                {savedConfig && (
                    <div className="space-y-6">
                        <div className="bg-white rounded-2xl shadow-lg p-6">
                            <h2 className="text-xl font-semibold text-gray-800 mb-4">Configuration Summary</h2>
                            
                            {/* Categories Section */}
                            {[2, 3, 4, 5, 6, 7, 8, 9].includes(selectedTemplate) && (
                                <div className="mb-8">
                                    <h3 className="text-lg font-semibold text-gray-700 mb-4 pb-2 border-b">
                                        Product Categories
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                                        {sets.map(set => {
                                            const hasContent = set.name || set.gender || set.type || set.category || set.image.preview;
                                            if (!hasContent) return null;
                                            
                                            return (
                                                <div key={set.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                    <h4 className="font-semibold text-gray-800 mb-2">
                                                        {set.name || `Category ${set.id}`}
                                                    </h4>
                                                    <div className="space-y-1 text-sm">
                                                        {set.gender && (
                                                            <p className="text-gray-600">
                                                                <span className="font-medium">Gender:</span> {set.gender}
                                                            </p>
                                                        )}
                                                        {set.type && (
                                                            <p className="text-gray-600">
                                                                <span className="font-medium">Type:</span> {set.type}
                                                            </p>
                                                        )}
                                                        {set.category && (
                                                            <p className="text-gray-600">
                                                                <span className="font-medium">Category:</span> {set.category}
                                                            </p>
                                                        )}
                                                    </div>
                                                    {set.image.preview && (
                                                        <img 
                                                            src={set.image.preview} 
                                                            alt={set.name || `Category ${set.id}`}
                                                            className="w-full h-32 object-cover rounded-lg mt-3 cursor-pointer hover:opacity-80"
                                                            onClick={() => setPreviewImage(set.image.preview)}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Banner Images Section */}
                            {[3, 6, 7].includes(selectedTemplate) && bannerImages.some(b => b.preview) && (
                                <div className="mb-8">
                                    <h3 className="text-lg font-semibold text-gray-700 mb-4 pb-2 border-b">
                                        Banner Images
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {bannerImages.filter(b => b.preview).map(banner => (
                                            <div key={banner.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                <h4 className="font-medium text-gray-700 mb-2">Banner {banner.id}</h4>
                                                <img 
                                                    src={banner.preview} 
                                                    alt={`Banner ${banner.id}`}
                                                    className="w-full h-40 object-cover rounded-lg cursor-pointer hover:opacity-80"
                                                    onClick={() => setPreviewImage(banner.preview)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Sliding Banners Section */}
                            {[4, 5, 8, 9].includes(selectedTemplate) && slidingBanners.some(b => b.preview) && (
                                <div className="mb-8">
                                    <h3 className="text-lg font-semibold text-gray-700 mb-4 pb-2 border-b">
                                        Sliding Banners
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {slidingBanners.filter(b => b.preview).map(banner => (
                                            <div key={banner.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                <h4 className="font-medium text-gray-700 mb-2">Sliding Banner {banner.id}</h4>
                                                <img 
                                                    src={banner.preview} 
                                                    alt={`Sliding Banner ${banner.id}`}
                                                    className="w-full h-40 object-cover rounded-lg cursor-pointer hover:opacity-80"
                                                    onClick={() => setPreviewImage(banner.preview)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Promotional Banners Section */}
                            {[4, 5, 8, 9].includes(selectedTemplate) && promoBanners.some(b => b.text || b.sub_text || b.image.preview) && (
                                <div className="mb-8">
                                    <h3 className="text-lg font-semibold text-gray-700 mb-4 pb-2 border-b">
                                        Promotional Banners
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {promoBanners.map(banner => {
                                            const hasContent = banner.text || banner.sub_text || banner.gender || banner.type || banner.category || banner.image.preview;
                                            if (!hasContent) return null;
                                            
                                            return (
                                                <div key={banner.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                    <h4 className="font-semibold text-gray-800 mb-2">
                                                        Promo Banner {banner.id}
                                                    </h4>
                                                    <div className="space-y-1 text-sm mb-3">
                                                        {banner.text && (
                                                            <p className="text-gray-700 font-medium">{banner.text}</p>
                                                        )}
                                                        {banner.sub_text && (
                                                            <p className="text-gray-600 italic">{banner.sub_text}</p>
                                                        )}
                                                        {banner.gender && (
                                                            <p className="text-gray-600">
                                                                <span className="font-medium">Gender:</span> {banner.gender}
                                                            </p>
                                                        )}
                                                        {banner.type && (
                                                            <p className="text-gray-600">
                                                                <span className="font-medium">Type:</span> {banner.type}
                                                            </p>
                                                        )}
                                                        {banner.category && (
                                                            <p className="text-gray-600">
                                                                <span className="font-medium">Category:</span> {banner.category}
                                                            </p>
                                                        )}
                                                    </div>
                                                    {banner.image.preview && (
                                                        <img 
                                                            src={banner.image.preview} 
                                                            alt={`Promo Banner ${banner.id}`}
                                                            className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-80"
                                                            onClick={() => setPreviewImage(banner.image.preview)}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Fashion Trends Section */}
                            {[4, 5, 8, 9].includes(selectedTemplate) && fashionTrends.some(t => t.text || t.image.preview) && (
                                <div className="mb-8">
                                    <h3 className="text-lg font-semibold text-gray-700 mb-4 pb-2 border-b">
                                        Fashion Trends
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                                        {fashionTrends.map(trend => {
                                            const hasContent = trend.text || trend.gender || trend.type || trend.category || trend.image.preview;
                                            if (!hasContent) return null;
                                            
                                            return (
                                                <div key={trend.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                    <h4 className="font-semibold text-gray-800 mb-2">
                                                        Trend {trend.id}
                                                    </h4>
                                                    <div className="space-y-1 text-sm mb-3">
                                                        {trend.text && (
                                                            <p className="text-gray-700 font-medium">{trend.text}</p>
                                                        )}
                                                        {trend.gender && (
                                                            <p className="text-gray-600">
                                                                <span className="font-medium">Gender:</span> {trend.gender}
                                                            </p>
                                                        )}
                                                        {trend.type && (
                                                            <p className="text-gray-600">
                                                                <span className="font-medium">Type:</span> {trend.type}
                                                            </p>
                                                        )}
                                                        {trend.category && (
                                                            <p className="text-gray-600">
                                                                <span className="font-medium">Category:</span> {trend.category}
                                                            </p>
                                                        )}
                                                    </div>
                                                    {trend.image.preview && (
                                                        <img 
                                                            src={trend.image.preview} 
                                                            alt={`Trend ${trend.id}`}
                                                            className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-80"
                                                            onClick={() => setPreviewImage(trend.image.preview)}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Videos Section */}
                            {[4, 5, 8, 9].includes(selectedTemplate) && videos.some(v => v.preview) && (
                                <div className="mb-8">
                                    <h3 className="text-lg font-semibold text-gray-700 mb-4 pb-2 border-b">
                                        Videos
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">
                                        {videos.filter(v => v.preview).map(video => (
                                            <div key={video.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                <h4 className="font-medium text-gray-700 mb-2">Video {video.id}</h4>
                                                <video
                                                    src={video.preview}
                                                    className="w-full h-32 object-cover rounded-lg"
                                                    controls
                                                    muted
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Empty State */}
                            {selectedTemplate === 1 && (
                                <div className="text-center py-8 text-gray-600">
                                    <p>This template doesn't require any configuration.</p>
                                    <p className="text-sm mt-2">Your classic template is ready to use!</p>
                                </div>
                            )}
                            {[10, 11, 12, 13, 14].includes(selectedTemplate) && (
                                <div className="text-center py-8 text-gray-600">
                                    <p>No configuration data added yet.</p>
                                    <p className="text-sm mt-2">Click "Edit" to add content to your template!</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (isLoading) return <Base Content={renderSkeleton()} />;

    return (
        <Base Content={
            <div className="min-h-screen bg-gray-50">
                {/* Image Preview Modal */}
                {previewImage && (
                    <div 
                        className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
                        onClick={() => setPreviewImage(null)}
                    >
                        <div className="relative max-w-4xl max-h-4xl">
                            <img src={previewImage} alt="Preview" className="max-w-full max-h-full object-contain" />
                            <button
                                onClick={() => setPreviewImage(null)}
                                className="absolute top-4 right-4 bg-white text-black rounded-full p-2 hover:bg-gray-200"
                            >
                                <X size={20} />
                            </button>
                        </div>
                    </div>
                )}

                {/* Step Progress Indicator */}
                {selectedTemplate && currentStep !== 'select' && (
                    <div className="max-w-7xl mx-auto px-6 pt-6">
                        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                            <div className="flex items-center justify-between">
                                {/* Step 1: Select Template */}
                                <div className="flex items-center">
                                    <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                                        currentStep === 'select' ? 'bg-blue-500 text-white' :
                                        ['configure', 'review', 'view'].includes(currentStep) ? 'bg-green-500 text-white' :
                                        'bg-gray-300 text-gray-600'
                                    }`}>
                                        {['configure', 'review', 'view'].includes(currentStep) ? '✓' : '1'}
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm font-semibold text-gray-700">Select Template</p>
                                        <p className="text-xs text-gray-500">Choose your design</p>
                                    </div>
                                </div>

                                {/* Connector Line */}
                                <div className={`flex-1 h-1 mx-4 ${
                                    ['configure', 'review', 'view'].includes(currentStep) ? 'bg-green-500' : 'bg-gray-300'
                                }`}></div>

                                {/* Step 2: Configure */}
                                <div className="flex items-center">
                                    <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                                        currentStep === 'configure' ? 'bg-blue-500 text-white' :
                                        ['review', 'view'].includes(currentStep) ? 'bg-green-500 text-white' :
                                        'bg-gray-300 text-gray-600'
                                    }`}>
                                        {['review', 'view'].includes(currentStep) ? '✓' : '2'}
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm font-semibold text-gray-700">Configure</p>
                                        <p className="text-xs text-gray-500">Fill in details</p>
                                    </div>
                                </div>

                                {/* Connector Line */}
                                <div className={`flex-1 h-1 mx-4 ${
                                    ['review', 'view'].includes(currentStep) ? 'bg-green-500' : 'bg-gray-300'
                                }`}></div>

                                {/* Step 3: Review */}
                                <div className="flex items-center">
                                    <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                                        currentStep === 'review' ? 'bg-blue-500 text-white' :
                                        currentStep === 'view' ? 'bg-green-500 text-white' :
                                        'bg-gray-300 text-gray-600'
                                    }`}>
                                        {currentStep === 'view' ? '✓' : '3'}
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm font-semibold text-gray-700">Review</p>
                                        <p className="text-xs text-gray-500">Check your details</p>
                                    </div>
                                </div>

                                {/* Connector Line */}
                                <div className={`flex-1 h-1 mx-4 ${
                                    currentStep === 'view' ? 'bg-green-500' : 'bg-gray-300'
                                }`}></div>

                                {/* Step 4: Saved */}
                                <div className="flex items-center">
                                    <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                                        currentStep === 'view' ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                                    }`}>
                                        {currentStep === 'view' ? '✓' : '4'}
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm font-semibold text-gray-700">Complete</p>
                                        <p className="text-xs text-gray-500">Configuration saved</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Main Content */}
                {currentStep === 'select' && renderTemplateSelection()}
                {currentStep === 'configure' && renderConfigurationStep()}
                {currentStep === 'review' && renderReviewStep()}
                {currentStep === 'view' && renderViewStep()}
            </div>
        } />
    );
};

export default Templates;